"use client";

import { useCommonAccountModal } from "@/Areas/Common/Pages/CommonAccount";
import {
  CenterAlerts,
  CommonSelect,
  If,
  LanguageSelector,
  SmartImage,
  SmartLink,
} from "@/Components";
import { useRouter } from "@/Components/AppProgressBar";
import { ThemeSegmented } from "@/Components/Segmenteds";
import { Helper, Roles } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import {
  useAccount,
  useApp,
  useAppSetting,
  useBodyClass,
  useCheckRole,
  useHtmlClass,
  useImageFallback,
  useResponsive,
  useSeoRouteContext,
  useServerRequestContext,
  useT,
} from "@/Hooks";
import { HeaderSectionProps } from "@/Templates/SectionsProps";
import { Icon } from "@iconify/react";
import { Avatar, Button, Dropdown, Space, Spin, Typography } from "antd";
import _ from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { FaEnvelope, FaPhoneAlt, FaQuestion, FaTimes } from "react-icons/fa";
import { FiMapPin, FiSearch } from "react-icons/fi";
import {
  RiBuildingLine,
  RiHomeLine,
  RiLockPasswordLine,
  RiLogoutBoxRLine,
  RiUserLine,
  RiUserStarLine,
} from "react-icons/ri";
import { DarkModeToggle, HeaderCartButton } from ".";
import HeaderMenu from "./HeaderMenu";

export type HeaderSectionVariant =
  | "home-one"
  | "home-two"
  | "home-three"
  | "home-four"
  | "home-five"
  | "home-six"
  | "home-seven"
  | "home-eight"
  | "home-nine"
  | "home-ten"
  | "home-eleven"
  | "home-twelve"
  | "home-thirteen"
  | "home-fourteen"
  | "home-fifteen"
  | "home-sixteen"
  | "home-seventeen"
  | "home-eighteen";

export type HeaderSectionComponentProps = HeaderSectionProps;

const HeaderAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateHeaderAppearance;

const preventHash =
  (fn?: () => void) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    fn?.();
  };

function resolveHeaderVariant(
  props: HeaderSectionComponentProps,
): HeaderSectionVariant {
  switch (props.setting.Appearance?.Key) {
    case HeaderAppearance.Home1.key:
      return "home-one";
    case HeaderAppearance.Home2.key:
      return "home-two";
    case HeaderAppearance.Home3.key:
      return "home-three";
    case HeaderAppearance.Home4.key:
      return "home-four";
    case HeaderAppearance.Home5.key:
      return "home-five";
    case HeaderAppearance.Home6.key:
      return "home-six";
    case HeaderAppearance.Home7.key:
      return "home-seven";
    case HeaderAppearance.Home8.key:
      return "home-eight";
    case HeaderAppearance.Home9.key:
      return "home-nine";
    case HeaderAppearance.Home10.key:
      return "home-ten";
    case HeaderAppearance.Home11.key:
      return "home-eleven";
    case HeaderAppearance.Home12.key:
      return "home-twelve";
    case HeaderAppearance.Home13.key:
      return "home-thirteen";
    case HeaderAppearance.Home14.key:
      return "home-fifteen";
    default:
      return "home-eight";
  }
}

function resolveAppearanceKey(props: HeaderSectionComponentProps): string {
  return props.setting.Appearance?.Key ?? HeaderAppearance.Home8.key;
}

function isSimpleAppearance(appearanceKey: string): boolean {
  switch (appearanceKey) {
    case HeaderAppearance.Home3.key:
    case HeaderAppearance.Home5.key:
    case HeaderAppearance.Home6.key:
    case HeaderAppearance.Home8.key:
    case HeaderAppearance.Home9.key:
    case HeaderAppearance.Home10.key:
    case HeaderAppearance.Home11.key:
    case HeaderAppearance.Home12.key:
    case HeaderAppearance.Home14.key:
      return true;
    default:
      return false;
  }
}

const HeaderSection: FC<HeaderSectionComponentProps> = ({
  pageData,
  setting,
  lists,
}) => {
  const resolvedVariant = resolveHeaderVariant({ pageData, setting, lists });
  const appearanceKey = resolveAppearanceKey({ pageData, setting, lists });
  const { profile, isSignIn, loading, signOut, isCenterPersonnel } =
    useAccount();
  const t = useT();
  const router = useRouter();
  const { buildSeoListPath } = useSeoRouteContext();
  const { menuOpen, menuToggle } = useAppSetting();
  const commonAccountModal = useCommonAccountModal();
  const { isMobile } = useResponsive();
  const serverRequestContext = useServerRequestContext();
  const { modal } = useApp();
  const isDoctor = useCheckRole(Roles.Provider);
  const getImageFallback = useImageFallback();
  useHtmlClass(menuOpen ? "menu-opened" : []);
  useBodyClass(menuOpen ? "menu-opned" : []);

  const baseHeaderClass = useMemo(() => {
    switch (resolvedVariant) {
      case "home-three":
        return "header header-trans header-three";
      case "home-five":
        return "header header-five";
      case "home-six":
        return "header header-six";
      case "home-eight":
        return "header header-trans header-three header-eight";
      case "home-ten":
        return "header header-ten home-menu-11";
      case "home-fifteen":
        return "header header-fifteen";
      case "home-sixteen":
        return "header header-sixteen";
      case "home-seventeen":
        return "header header-seventeen";
      case "home-fourteen":
        return "header header-fourteen home-menu";
      case "home-eighteen":
        return "header header-eighteen";
      default:
        return "header header-trans header-three header-eight";
    }
  }, [resolvedVariant]);

  const [headerClass, setHeaderClass] = useState(baseHeaderClass);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setHeaderClass(baseHeaderClass);
  }, [baseHeaderClass]);

  const alerts = (
    <CenterAlerts
      CenterId={pageData.CenterId}
      CenterSiteTemplatePageId={pageData.Id}
    />
  );

  const logo = pageData.CommonData.Image?.Url ? (
    <SmartImage
      cover
      width={50}
      height={50}
      src={pageData.CommonData.Image.Url}
      className="img-fluid"
      alt={pageData.CommonData.Image.AltText ?? pageData.CommonData.Name}
    />
  ) : undefined;

  const logoWithName = (
    <div className={`logoWithName variant-${resolvedVariant}`}>
      <div className="logoBox">{logo}</div>
      <h2 className="h3 logoTitle">
        <bdi dir="auto">{pageData.CommonData.Name}</bdi>
      </h2>
    </div>
  );

  const menuHeader = (
    <div className="menu-header">
      <div>
        <div className="logoBox">{logo}</div>
      </div>
      <Space>
        <ThemeSegmented />
        <LanguageSelector iconOnly />
      </Space>
      <SmartLink
        id="menu_close"
        className="menu-close"
        href="#"
        onClick={() => menuToggle()}
      >
        <Typography.Text>
          <Button icon={<FaTimes />} shape="circle" />
        </Typography.Text>
      </SmartLink>
    </div>
  );

  const signInButtons = (
    <Space wrap>
      {loading ? (
        <Spin size="small" />
      ) : isSignIn ? (
        <>
          <Dropdown
            menu={{
              items: [
                ...Helper.addItem(
                  {
                    key: "Panel",
                    icon: <RiHomeLine />,
                    label: (
                      <SmartLink href="/Panel">
                        {t(
                          "landing.header.personnelArea",
                          undefined,
                          "Personnel area",
                        )}
                      </SmartLink>
                    ),
                  },
                  isCenterPersonnel &&
                  serverRequestContext.CenterTypeKey !=
                  CenterOptions.Enum.CenterType.Normal.key,
                ),
                {
                  key: "Customer",
                  icon: <RiHomeLine />,
                  label: (
                    <SmartLink href="/Customer/Dashboard">
                      {t(
                        "landing.header.customerArea",
                        undefined,
                        "Customer area",
                      )}
                    </SmartLink>
                  ),
                },
                {
                  key: "profile",
                  icon: <RiUserLine />,
                  label: (
                    <SmartLink
                      href={
                        isCenterPersonnel
                          ? serverRequestContext.CenterTypeKey ==
                            CenterOptions.Enum.CenterType.Normal.key
                            ? "/personnel-area-unavailable"
                            : "/Panel/Common/Account/Profile"
                          : "/Customer/Account/Profile"
                      }
                    >
                      {t(
                        "landing.header.editProfile",
                        undefined,
                        "Edit profile",
                      )}
                    </SmartLink>
                  ),
                },
                ...Helper.addItem(
                  {
                    key: "password",
                    icon: <RiLockPasswordLine />,
                    label: t(
                      "landing.header.passwordActions",
                      undefined,
                      "Password actions",
                    ),
                    onClick: () => commonAccountModal.showPasswordSetting(),
                  },
                  isCenterPersonnel,
                ),
                ...Helper.addItem(
                  {
                    key: "DoctorProfile",
                    icon: <RiUserStarLine />,
                    label: (
                      <SmartLink href={profile?.CenterUser?.Link}>
                        {t("landing.header.myProfile", undefined, "My profile")}
                      </SmartLink>
                    ),
                  },
                  isDoctor && !!profile?.CenterUser?.Link,
                ),
                ...Helper.addItem(
                  {
                    key: "CenterProfile",
                    icon: <RiBuildingLine />,
                    label: (
                      <SmartLink href={profile?.Center?.Link}>
                        {t(
                          "landing.header.centerProfile",
                          undefined,
                          "Center profile",
                        )}
                      </SmartLink>
                    ),
                  },
                  !!profile?.Center?.Link,
                ),
                {
                  key: "logout",
                  icon: <RiLogoutBoxRLine />,
                  label: t("landing.header.signOut", undefined, "Sign out"),
                  onClick: async () => {
                    if (
                      await modal.confirm({
                        title: t(
                          "landing.header.signOut",
                          undefined,
                          "Sign out",
                        ),
                        content: t(
                          "landing.header.signOutConfirm",
                          undefined,
                          "Do you want to sign out of your account?",
                        ),
                        okText: t("landing.common.yes", undefined, "Yes"),
                        cancelText: t("landing.common.no", undefined, "No"),
                        centered: true,
                      })
                    ) {
                      signOut();
                    }
                  },
                },
              ],
            }}
          >
            {profile &&
              (isMobile ? (
                <Avatar
                  src={getImageFallback({
                    imageUrl: profile.ImageUrl,
                    entity: "User",
                    genderKey: profile.GenderKey,
                    isDoctor,
                  })}
                />
              ) : (
                <Button
                  icon={
                    <Avatar
                      src={getImageFallback({
                        imageUrl: profile.ImageUrl,
                        entity: "User",
                        genderKey: profile.GenderKey,
                        isDoctor,
                      })}
                    />
                  }
                >
                  <Typography.Text>{profile.FullName}</Typography.Text>
                </Button>
              ))}
          </Dropdown>
          <HeaderCartButton />
        </>
      ) : (
        <button
          className="btn log-btn"
          onClick={() => commonAccountModal.showSignIn()}
        >
          {t("landing.header.signInOrSignUp", undefined, "Sign in / Sign up")}
        </button>
      )}
    </Space>
  );

  const menuItems = pageData.Menus.find((m) =>
    m.CenterSiteTemplateMenuLocationTypeKeys.includes(
      isMobile
        ? CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
          .MobileMenu.key
        : CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.MainMenu
          .key,
    ),
  )?.Items;
  const menu = <HeaderMenu menuItems={menuItems} />;

  useEffect(() => {
    if (!isSimpleAppearance(appearanceKey)) {
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const scrollThreshold = 100;

      if (scrollPosition > scrollThreshold) {
        setHeaderClass(`${baseHeaderClass} pharmacy-header`);
      } else {
        setHeaderClass(baseHeaderClass);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [appearanceKey, baseHeaderClass]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  switch (appearanceKey) {
    case HeaderAppearance.Home3.key:
    case HeaderAppearance.Home5.key:
    case HeaderAppearance.Home6.key:
    case HeaderAppearance.Home8.key:
    case HeaderAppearance.Home9.key:
    case HeaderAppearance.Home10.key:
    case HeaderAppearance.Home11.key:
    case HeaderAppearance.Home12.key:
    case HeaderAppearance.Home14.key:
      return (
        <>
          <header className={`${headerClass} variant-${resolvedVariant}`}>
            <div className="container">
              <nav className="navbar navbar-expand-lg header-nav">
                <div className="navbar-header">
                  <SmartLink
                    id="mobile_btn"
                    href="#"
                    onClick={() => menuToggle()}
                  >
                    <span className="bar-icon">
                      <span />
                      <span />
                      <span />
                    </span>
                  </SmartLink>
                  <SmartLink href="/" className="navbar-brand logo">
                    {logoWithName}
                  </SmartLink>
                </div>
                <div className="main-menu-wrapper">
                  {menuHeader}
                  <ul className="main-nav">{menu}</ul>
                </div>
                <ul className="nav header-navbar-rht">
                  <li className="header-theme me-1">
                    <DarkModeToggle />
                  </li>
                  <li>
                    <LanguageSelector iconOnly={isMobile} />
                  </li>
                  <If condition={!_.isEmpty(pageData.CommonData.Tels)}>
                    <li className="contact-item">
                      <Space>
                        <FaPhoneAlt />
                        <span>
                          {t("landing.header.contact", undefined, "Contact:")}
                        </span>
                        {pageData.CommonData.Tels.map((tel) => (
                          <SmartLink key={tel} href={`tel:${tel}`}>
                            {tel}
                          </SmartLink>
                        ))}
                      </Space>
                    </li>
                  </If>
                  <li className="nav-item">{signInButtons}</li>
                </ul>
              </nav>
            </div>
          </header>
          {alerts}
        </>
      );
    case HeaderAppearance.Home1.key:
    case HeaderAppearance.Home2.key:
    case HeaderAppearance.Home4.key:
    case HeaderAppearance.Home7.key:
      return (
        <>
          <header
            className={`header header-custom header-fixed header-ten home-care-header dentist-header variant-${resolvedVariant} ${isScrolled ? "pharmacy-header header-space" : ""
              }`}
          >
            <div className="header-top-wrap">
              <div className="container">
                <div className="header-top-bar">
                  <ul className="header-contact p-0 m-0">
                    <li>
                      <span className="question-mark-icon">
                        <FaQuestion />
                      </span>
                      {setting.Title}
                    </li>
                    <If condition={!!pageData.CommonData.Email}>
                      <li>
                        <Space>
                          <FaEnvelope />
                          <SmartLink href={`mail:${pageData.CommonData.Email}`}>
                            {pageData.CommonData.Email}
                          </SmartLink>
                        </Space>
                      </li>
                    </If>
                    <If condition={!_.isEmpty(pageData.CommonData.Tels)}>
                      {pageData.CommonData.Tels.map((tel) => (
                        <li key={tel}>
                          <Space>
                            <FaPhoneAlt />
                            <SmartLink href={`tel:${tel}`}>{tel}</SmartLink>
                          </Space>
                        </li>
                      ))}
                    </If>
                  </ul>
                  <ul className="social-icon m-0 p-0">
                    <li>{signInButtons}</li>
                    <li>
                      {pageData.CommonData.SocialNetworks.map(
                        (socialNetwork) => (
                          <SmartLink
                            key={socialNetwork.Id}
                            href={socialNetwork.Link}
                            title={socialNetwork.Name}
                          >
                            <Icon icon={socialNetwork.Icon} />
                          </SmartLink>
                        ),
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="container">
              <nav className="navbar navbar-expand-lg header-nav">
                <div className="navbar-header">
                  <SmartLink
                    id="mobile_btn"
                    href="#"
                    onClick={preventHash(() => menuToggle())}
                  >
                    <span className="bar-icon">
                      <span />
                      <span />
                      <span />
                    </span>
                  </SmartLink>
                  <SmartLink href="/" className="navbar-brand logo">
                    {logoWithName}
                  </SmartLink>
                </div>
                <div className="main-menu-wrapper">
                  {menuHeader}
                  <ul className="main-nav">
                    {menu}
                    {loading ? (
                      <Spin
                        indicator={
                          <Icon
                            icon="eos-icons:loading"
                            width="18"
                            height="18"
                          />
                        }
                      />
                    ) : isSignIn ? (
                      <>
                        <li className="login-link">
                          <SmartLink href="/Customer/Dashboard">
                            {t(
                              "landing.header.customerArea",
                              undefined,
                              "Customer area",
                            )}
                          </SmartLink>
                        </li>
                        {isCenterPersonnel && (
                          <li className="login-link">
                            <SmartLink href="/Panel">
                              {t(
                                "landing.header.personnelArea",
                                undefined,
                                "Personnel area",
                              )}
                            </SmartLink>
                          </li>
                        )}
                      </>
                    ) : (
                      <li className="login-link">
                        <SmartLink href="/SignIn">
                          {t(
                            "landing.header.signInOrSignUp",
                            undefined,
                            "Sign in / Sign up",
                          )}
                        </SmartLink>
                      </li>
                    )}
                  </ul>
                </div>
                <ul className="nav header-navbar-rht">
                  <li className="header-theme me-3">
                    <DarkModeToggle />
                  </li>
                  <li>
                    <LanguageSelector iconOnly={isMobile} />
                  </li>
                </ul>
              </nav>
            </div>
          </header>
          {alerts}
        </>
      );
    case HeaderAppearance.Home13.key:
      return (
        <>
          <div className={`top-header variant-${resolvedVariant}`}>
            <div className="container">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="special-offer-content">
                    <p>{setting.Title}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="top-header-right">
                    <ul className="nav">
                      <li className="header-theme me-0 pe-0">
                        <DarkModeToggle />
                      </li>
                      <li>
                        <LanguageSelector iconOnly={isMobile} />
                      </li>
                      <li>{signInButtons}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`cart-section variant-${resolvedVariant}`}>
            <div className="container">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <div className="cart-logo">
                    <SmartLink href="/">{logoWithName}</SmartLink>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="cart-search">
                    <form>
                      <div className="enter-pincode">
                        <FiMapPin />
                        <div className="enter-pincode-input">
                          <input
                            type="text"
                            className="form-control"
                            placeholder={t(
                              "landing.header.postalCodePlaceholder",
                              undefined,
                              "Enter postal code",
                            )}
                          />
                        </div>
                      </div>
                      <div className="cart-search-input">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t(
                            "landing.header.commerceSearchPlaceholder",
                            undefined,
                            "Search medicines, health products and more",
                          )}
                        />
                      </div>
                      <div className="cart-search-btn">
                        <button type="submit" className="btn">
                          <FiSearch />
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="shopping-cart-list">
                    <ul className="nav">
                      <li>
                        <SmartLink href="#">
                          <SmartImage
                            src="/Templates/Doccure/img/icons/cart-favourite.svg"
                            alt=""
                            width={24}
                            height={24}
                          />
                        </SmartLink>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <header
            className={`${isScrolled ? "pharmacy-header" : ""} header variant-${resolvedVariant}`}
          >
            <div className="container">
              <nav className="navbar navbar-expand-lg header-nav">
                <div className="navbar-header">
                  <SmartLink
                    id="mobile_btn"
                    href="#"
                    onClick={() => menuToggle()}
                  >
                    <span className="bar-icon">
                      <span />
                      <span />
                      <span />
                    </span>
                  </SmartLink>
                </div>
                <div>
                  <CommonSelect
                    sourceType="Landing"
                    methodName="Departments"
                    model={{ HasCenterBranches: true }}
                    allowClear
                    showSearch
                    style={{ minWidth: 260 }}
                    size="large"
                    placeholder={t(
                      "landing.header.browseCategories",
                      undefined,
                      "Browse categories",
                    )}
                    onChange={(value) => {
                      if (value == null || Array.isArray(value)) {
                        return;
                      }

                      router.push(
                        buildSeoListPath("centers", `departmentIds=${value}`),
                      );
                    }}
                  />
                </div>
                <div className="main-menu-wrapper">
                  {menuHeader}
                  <ul className="main-nav">{menu}</ul>
                </div>
              </nav>
            </div>
          </header>
          {alerts}
        </>
      );
    default:
      return null;
  }
};

export default HeaderSection;
